import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Paper,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SummarizeIcon from '@mui/icons-material/Summarize';
import apiService from '../../../services/api';
import { dashboardTheme } from '../styles/dashboardTheme';

/**
 * SessionSummaryDialog Component
 * Modal for viewing session summary and reflection prompts
 */
const SessionSummaryDialog = ({ open, onClose, token }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [summaryData, setSummaryData] = useState(null);
  const [conversationId, setConversationId] = useState(null);

  useEffect(() => {
    if (open && token) {
      fetchActiveConversationSummary();
    }
  }, [open, token]);

  const fetchActiveConversationSummary = async () => {
    setLoading(true);
    setError('');
    setSummaryData(null);

    try {
      // First, get the conversation history to find the active conversation
      const historyResponse = await apiService.getHistory(token);
      
      if (!historyResponse.conversationId) {
        setError('No active conversation found. Start a chat session first.');
        setLoading(false);
        return;
      }

      setConversationId(historyResponse.conversationId);

      // Now fetch the session summary for this conversation
      try {
        const summaryResponse = await apiService.getClientSessionSummary(
          historyResponse.conversationId,
          token
        );
        
        setSummaryData(summaryResponse);
      } catch (summaryError) {
        if (summaryError.response?.status === 404) {
          setError('No summary has been generated for this session yet. Session summaries are created after meaningful conversations with your therapist.');
        } else {
          setError('Unable to load session summary. Please try again later.');
        }
      }
    } catch (err) {
      console.error('Error fetching session summary:', err);
      setError('Unable to connect to the server. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateSummary = async () => {
    if (!conversationId) return;
    
    setLoading(true);
    setError('');

    try {
      // Generate a new summary
      await apiService.generateSessionSummary(conversationId, token);
      
      // Fetch the newly generated summary
      const summaryResponse = await apiService.getClientSessionSummary(
        conversationId,
        token
      );
      
      setSummaryData(summaryResponse);
    } catch (err) {
      console.error('Error generating summary:', err);
      setError('Unable to generate summary. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SummarizeIcon sx={{ color: dashboardTheme.colors.primary.main }} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Session Summary
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center',
            py: 6 
          }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography color="text.secondary">
              Loading session summary...
            </Typography>
          </Box>
        ) : error ? (
          <Box sx={{ py: 4 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              {error}
            </Alert>
            {conversationId && !summaryData && (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  startIcon={<SummarizeIcon />}
                  onClick={handleGenerateSummary}
                  sx={{
                    borderRadius: '12px',
                    background: dashboardTheme.colors.primary.gradient,
                    '&:hover': {
                      background: dashboardTheme.colors.primary.dark
                    }
                  }}
                >
                  Generate Summary Now
                </Button>
              </Box>
            )}
          </Box>
        ) : summaryData ? (
          <Box>
            {/* Client Summary Section */}
            <Paper elevation={0} sx={{ 
              p: 3, 
              mb: 3,
              background: 'linear-gradient(135deg, rgba(255, 140, 0, 0.05) 0%, rgba(255, 140, 0, 0.02) 100%)',
              border: '1px solid rgba(255, 140, 0, 0.1)',
              borderRadius: '12px'
            }}>
              <Typography variant="h6" gutterBottom sx={{ 
                color: dashboardTheme.colors.primary.main,
                fontWeight: 600,
                mb: 2
              }}>
                Your Session Summary
              </Typography>
              <Typography variant="body1" sx={{ 
                lineHeight: 1.8,
                color: '#424242',
                whiteSpace: 'pre-wrap'
              }}>
                {summaryData.clientSummary || 'No summary available for this session.'}
              </Typography>
            </Paper>

            {/* Reflection Prompts Section */}
            {summaryData.reflectionPrompts && summaryData.reflectionPrompts.length > 0 && (
              <Paper elevation={0} sx={{ 
                p: 3,
                background: 'linear-gradient(135deg, rgba(66, 165, 245, 0.05) 0%, rgba(66, 165, 245, 0.02) 100%)',
                border: '1px solid rgba(66, 165, 245, 0.1)',
                borderRadius: '12px'
              }}>
                <Typography variant="h6" gutterBottom sx={{ 
                  color: dashboardTheme.colors.info.main,
                  fontWeight: 600,
                  mb: 2
                }}>
                  Reflection Prompts
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: '#666',
                  mb: 2,
                  fontStyle: 'italic'
                }}>
                  Consider these questions before your next session:
                </Typography>
                <Box component="ol" sx={{ pl: 2, m: 0 }}>
                  {summaryData.reflectionPrompts.map((prompt, index) => (
                    <Box component="li" key={index} sx={{ 
                      mb: 1.5,
                      color: '#424242',
                      '&::marker': {
                        color: dashboardTheme.colors.info.main,
                        fontWeight: 600
                      }
                    }}>
                      <Typography variant="body1">
                        {prompt}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            )}
          </Box>
        ) : (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <SummarizeIcon sx={{ fontSize: 64, color: '#e0e0e0', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No session summary available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Start a conversation to generate summaries
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          sx={{ borderRadius: '12px' }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionSummaryDialog;